import { invariantResponse } from '@epic-web/invariant'
import {
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Form, json, Link, useLoaderData } from '@remix-run/react'
import { Button } from '#app/components/ui/button.tsx'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card'
import { Icon } from '#app/components/ui/icon.js'
import { StatusButton } from '#app/components/ui/status-button.js'
import {
	Table,
	TableCell,
	TableHeader,
	TableRow,
} from '#app/components/ui/table'
import { prisma } from '#app/utils/db.server.js'
import { useDoubleCheck } from '#app/utils/misc.js'
import { requireUserWithRole } from '#app/utils/permissions.server.js'
import { redirectWithToast } from '#app/utils/toast.server.js'

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [{ title: `Kogia | ${data?.user.name}` }]
}

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
	await requireUserWithRole(request, 'admin')
	const user = await prisma.user.findUnique({
		where: { username: params.username },
		select: {
			name: true,
			instagramUrl: true,
			facebookUrl: true,
			twitterUrl: true,
			collections: {
				select: {
					id: true,
				},
			},
			company: true,
			biography: true,
			email: true,
			location: true,
			username: true,
			portfolioUrl: true,
		},
	})
	const application = await prisma.membershipApplication.findUnique({
		where: { email: user?.email },
	})
	invariantResponse(user, 'User not found', { status: 404 })
	return json({ user, application })
}

export function formatInstagramUrl(url: string) {
	if (url.startsWith('http') || url.startsWith('https')) return url
	if (url.startsWith('www') || url.startsWith('instagram')) {
		return `https://` + url
	}
	return `https://instagram.com/${url.replace('@', '')}`
}

export function formatTwitterUrl(url: string) {
	if (url.startsWith('http') || url.startsWith('https')) return url
	if (url.startsWith('www') || url.startsWith('twitter')) {
		return `https://` + url
	}
	return `https://twitter.com/${url.replace('@', '')}`
}

export function formatFacebookUrl(url: string) {
	if (url.startsWith('http') || url.startsWith('https')) return url
	if (url.startsWith('www') || url.startsWith('facebook')) {
		return `https://` + url
	}
	return `https://facebook.com/${url.replace('@', '')}`
}

export function formatPortfolioUrl(url: string) {
	if (url.startsWith('http') || url.startsWith('https')) return url
	if (url.startsWith('www')) {
		return `https://` + url
	}
	return url
}

export const action = async ({ request }: ActionFunctionArgs) => {
	const formData = await request.formData()
	const username = formData.get('username')
	const intent = formData.get('intent')
	if (intent === 'delete' && username && typeof username === 'string') {
		await prisma.user.delete({
			where: {
				username: username,
			},
		})
		return redirectWithToast('/admin/memberships', {
			type: 'success',
			title: 'Member deleted',
			description: 'Might be worth sending them an email',
		})
	}
	return redirectWithToast('/admin/members/' + username, {
		type: 'error',
		title: 'Member not deleted',
		description: 'Something went wrong',
	})
}

export default function ArtistsIndex() {
	const { user, application } = useLoaderData<typeof loader>()
	const deleteButton = useDoubleCheck()
	return (
		<div className="mx-auto flex w-full max-w-screen-xl flex-col gap-4 p-4">
			<Card>
				<CardHeader>
					<CardTitle>User Details</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-4">
					<Table>
						<TableHeader>
							<TableCell>Field</TableCell>
							<TableCell>Value</TableCell>
						</TableHeader>
						<TableRow>
							<TableCell>Name</TableCell>
							<TableCell>{user.name}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Username</TableCell>
							<TableCell>{user.username}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Location</TableCell>
							<TableCell>{user.location}</TableCell>
						</TableRow>
						{user.instagramUrl && (
							<TableRow>
								<TableCell>Instagram</TableCell>
								<TableCell>
									<a
										href={formatInstagramUrl(user.instagramUrl)}
										target="_blank"
										className="underline"
										rel="noreferrer"
									>
										{formatInstagramUrl(user.instagramUrl)}
									</a>
								</TableCell>
							</TableRow>
						)}
						<TableRow>
							<TableCell>Email</TableCell>
							<TableCell>
								{/* eslint-disable-next-line */}
								<a className="underline" href={`mailto:${user.email}`}>
									{user.email}
								</a>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Biography</TableCell>
							<TableCell>{user.biography}</TableCell>
						</TableRow>
					</Table>
				</CardContent>
			</Card>
			<Card>
				<CardHeader>
					<CardTitle>Company Details</CardTitle>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableCell>Field</TableCell>
							<TableCell>Value</TableCell>
						</TableHeader>
						<TableRow>
							<TableCell>Name</TableCell>
							<TableCell>{user.company?.name}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Location</TableCell>
							<TableCell>{user.company?.country}</TableCell>
						</TableRow>
					</Table>
				</CardContent>
			</Card>
			<Card>
				<CardHeader>
					<CardTitle>Action Items</CardTitle>
				</CardHeader>
				<CardContent>
					<Form method="POST" className="">
						<input
							type="hidden"
							readOnly
							value={user.username}
							name="username"
						/>
						<StatusButton
							{...deleteButton.getButtonProps({
								type: 'submit',
								name: 'intent',
								value: 'delete',
							})}
							variant={deleteButton.doubleCheck ? 'destructive' : 'destructive'}
							status={'idle'}
						>
							<Icon name="envelope-closed">
								{deleteButton.doubleCheck
									? `Are you sure?`
									: `Delete User Account`}
							</Icon>
						</StatusButton>
					</Form>
					<Button className="mt-8" asChild>
						<Link to={`/admin/applications/memberships/${application?.id}`}>
							View Application
						</Link>
					</Button>
				</CardContent>
			</Card>
		</div>
	)
}
